import type { ModuleStorage } from "@artis/sdk";
import { useCallback, useEffect, useMemo, useRef } from "react";

import {
  deserializeWorkbook,
  type PersistedWorkbook,
  serializeWorkbook,
  SCRATCHPAD_WORKBOOK_KEY,
  type Sheet,
} from "@package/scratchpad-utils";

type MarkPersisted = (sheets: Sheet[], activeSheetId: string) => void;

// Converges the local workbook with writes from the same user's other tabs or devices. It subscribes to the workbook key on the user scoped store, and anything delivered through subscribe is already a genuine remote change because the SDK store swallows this pane's own writes by rev. On a remote value: if it equals the current state it does nothing; if the user is mid edit (an open cell editor or draft) it holds the value and applies it once the edit ends; otherwise it deserializes and replaces the workbook. markPersisted is called after applying so autosave treats the just applied value as already saved and does not echo it straight back to the server. Last write wins, with no merge and no conflict UI (v1). A null tombstone or unparseable value is ignored.
export function useRemoteWorkbook({
  storage,
  sheets,
  activeSheetId,
  editing,
  replaceWorkbook,
  markPersisted,
}: {
  storage: ModuleStorage;
  sheets: Sheet[];
  activeSheetId: string;
  editing: boolean;
  replaceWorkbook: (workbook: PersistedWorkbook) => void;
  markPersisted: MarkPersisted;
}) {
  const current = useMemo(() => serializeWorkbook(sheets, activeSheetId), [sheets, activeSheetId]);
  const currentRef = useRef(current);
  currentRef.current = current;
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const heldRef = useRef<string | null>(null);

  const apply = useCallback(
    (value: string) => {
      const workbook = deserializeWorkbook(value);
      if (!workbook) return;
      replaceWorkbook(workbook);
      markPersisted(workbook.sheets, workbook.activeSheetId);
    },
    [replaceWorkbook, markPersisted],
  );

  useEffect(
    () =>
      storage.subscribe(SCRATCHPAD_WORKBOOK_KEY, (value) => {
        if (typeof value !== "string") return;
        if (value === currentRef.current) return;
        if (editingRef.current) {
          heldRef.current = value;
          return;
        }
        apply(value);
      }),
    [storage, apply],
  );

  // Flush a value that arrived mid-edit once the edit ends.
  useEffect(() => {
    if (editing) return;
    const held = heldRef.current;
    if (held === null) return;
    heldRef.current = null;
    if (held === currentRef.current) return;
    apply(held);
  }, [editing, apply]);
}
